import {useEvent} from 'Event/EventProvider'
import {usePoints} from 'Event/PointsProvider'
import {ValidationError} from 'lib/ui/api-client'
import {useAsync} from 'lib/async'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import {api} from 'lib/url'
import {Form} from 'organization/Event/FormsProvider'
import {
  isQuestionWithOptions,
  Option,
  Question,
} from 'organization/Event/QuestionsProvider'
import React, {useCallback, useEffect, useState} from 'react'

export interface Answer {
  id: string | null
  form_id: number
  question_id: number
  value: string
}

interface SubmissionsContextProps {
  submit: (form: Form, data: {answers: Answer[]}) => Promise<void>
  answers: Answer[]
  responseError: ValidationError<any> | null
  setAnswers: (answers: Answer[]) => void
}

export const SubmissionsContext = React.createContext<
  undefined | SubmissionsContextProps
>(undefined)

export default function SubmissionsProvider(props: {
  children: React.ReactElement
}) {
  const [answers, setAnswers] = useState<Answer[] | null>(null)
  const {loading, data: fetchedAnswers} = useAnswers()
  const [
    responseError,
    setResponseError,
  ] = useState<ValidationError<any> | null>(null)
  const {submit: submitAction} = usePoints()
  const submitOptionActions = useSubmitOptionActions()

  useEffect(() => {
    if (!fetchedAnswers) {
      return
    }

    setAnswers(fetchedAnswers)
  }, [fetchedAnswers])

  const submit = useSubmit()

  const onSubmit = (form: Form, data: {answers: Answer[]}) => {
    setResponseError(null)

    return submit(form, data)
      .then((newAnswers) => {
        if (!answers) {
          return
        }
        /**
         * Update the answers that's been submitted...
         */
        const existing = answers.filter(
          (a) => !newAnswers.find((na) => na.question_id === a.question_id),
        )

        const updated = [...existing, ...newAnswers]
        setAnswers(updated)

        /**
         * Submit Form action
         */
        if (form.action) {
          submitAction(form.action.key)
        }

        submitOptionActions(form, newAnswers)
      })
      .catch((e) => {
        setResponseError(e)
        throw e // Re-throw to avoid the form thinking it was a success
      })
  }

  if (loading || !answers) {
    return <FullPageLoader />
  }

  return (
    <SubmissionsContext.Provider
      value={{submit: onSubmit, answers, setAnswers, responseError}}
    >
      {props.children}
    </SubmissionsContext.Provider>
  )
}

export function StaticSubmissionsProvider(props: {
  answers?: Answer[]
  children: React.ReactNode
}) {
  const {answers = []} = props

  return (
    <SubmissionsContext.Provider
      value={{
        answers,
        setAnswers: () => {},
        responseError: null,
        submit: (_form: Form, _answers: {answers: Answer[]}) =>
          Promise.resolve(),
      }}
    >
      {props.children}
    </SubmissionsContext.Provider>
  )
}

function useAnswers() {
  const {client, event} = useEvent()
  const url = api(`/events/${event.id}/submissions`)

  const request = useCallback(() => client.get<Answer[]>(url), [client, url])
  return useAsync(request)
}

function useSubmit() {
  const {client} = useEvent()

  return (form: Form, data: {answers: Answer[]}) => {
    const url = api(`/forms/${form.id}/submissions`)
    return client.post<Answer[]>(url, data)
  }
}

/**
 * Submit option actions (correct answers)
 */
function useSubmitOptionActions() {
  const {submit: submitAction} = usePoints()

  return (form: Form, answers: Answer[]) => {
    for (const question of form.questions) {
      const hasOptions =
        isQuestionWithOptions(question) && question.options.length > 0
      if (!hasOptions) {
        continue
      }

      const answer = answers.find((a) => a.question_id === question.id)
      if (!answer || !answer.value) {
        continue
      }

      submitOptionActions({
        answer: answer.value,
        options: question.options,
        submitAction,
      })
    }
  }
}

/**
 * Submit an action per option if one was defined.
 *
 * @param props
 * @returns
 */
function submitOptionActions(props: {
  options: Option[]
  answer: string
  submitAction: (actionkey: string | null) => void
}) {
  const {answer, options, submitAction} = props

  const values = answer.split(', ')

  for (const [i, option] of options.entries()) {
    const isSelected = values.includes(option.value)
    if (!isSelected) {
      continue
    }

    if (!option.action_key) {
      continue
    }

    // HACK - currently the backend is not able to handle multiple
    // actions at once (issue: https://app.asana.com/0/1191311659399184/1201922898964933/f)
    // The workaround is to only send a single action at any given
    // time, so we'll just add a delay here.
    setTimeout(() => {
      submitAction(option.action_key)
    }, i * 1000)
  }
}

export function useSubmissions() {
  const context = React.useContext(SubmissionsContext)
  if (context === undefined) {
    throw new Error('useSubmissions must be used within a SubmissionsProvider')
  }

  return context
}

export function findAnswer(question: Question, answers?: Answer[]) {
  if (!answers) {
    return undefined
  }

  const answer = answers.find((a) => a.question_id === question.id)
  if (!answer) {
    return undefined
  }

  return answer.value ?? ''
}

/**
 * Checks whether answers exist for a particular form.
 *
 * @param answers
 * @param form
 * @returns
 */
export function hasSubmission(answers: Answer[], form: Form) {
  /**
   * Answers could contain submissions from several forms so we need
   * to see if there are any relevant to this form.
   */
  const numSubmitted = form.questions.filter(
    (q) => findAnswer(q, answers) !== undefined,
  ).length

  return numSubmitted > 0
}
